import { useDrawerInvokers } from "@tiny/lib";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useWorkflowsBuilderPageView } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilderPage.context";
import {
  DelayFormValues,
  getBrowserTimeZone,
  getDelayFormState,
  listTimeZones,
  toDelayFormValues,
  toDelayNodeData,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/CustomNodes/Timing.helpers";

const useTimerDelayForm = ({ id }: { id: string }) => {
  const { updateNode, getNode, viewMode } = useWorkflowsBuilderPageView();
  const { hideDrawer } = useDrawerInvokers();
  const node = getNode(id);

  const browserTimeZone = React.useMemo(getBrowserTimeZone, []);
  // Missing fields fall back to the same defaults here and in the comparison
  // below, so a node saved before a field existed does not open with Save
  // enabled.
  const initialValues = React.useMemo(
    () => toDelayFormValues(node?.data, browserTimeZone),
    [node, browserTimeZone]
  );

  const methods = useForm<DelayFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
  });
  const { handleSubmit, control } = methods;
  const goBack = () => {
    hideDrawer();
  };

  const formValues = useWatch({ control }) as DelayFormValues;

  // Only the zone the node opened with (saved, or the browser's default) can
  // be missing from the list; anything picked afterwards came from it. Keyed
  // on that, the list - and the labels built from it - survive a zone change.
  const timeZoneOptions = React.useMemo(
    () => listTimeZones(initialValues.timeZone),
    [initialValues.timeZone]
  );

  const onSubmit = handleSubmit(async (data) => {
    updateNode({
      id,
      data: {
        ...toDelayNodeData(data),
        config: {
          configured: true,
        },
      },
    });
    hideDrawer();
  });

  const { saveDisabled: saveTimerDisabled, dateNote } = getDelayFormState(
    initialValues,
    formValues
  );

  return {
    goBack,
    onSubmit,
    methods,
    formValues,
    control,
    saveTimerDisabled,
    dateNote,
    timeZoneOptions,
    viewMode,
  };
};

export default useTimerDelayForm;
